import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import './AnalyticsPage.css';

const RANGE_OPTIONS = [
  { value: 7, label: '7 días' },
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
];

const PAGE_LABELS = {
  home: 'Inicio',
  jobs: 'Buscar empleo',
  job: 'Detalle de oferta',
  login: 'Iniciar sesión',
  register: 'Crear cuenta',
  dashboard: 'Mi cuenta (candidato)',
  'employer-dashboard': 'Panel de empresa',
  messages: 'Mensajes',
  chat: 'Chat',
  about: 'Quiénes somos',
  contact: 'Contacto',
  'post-job': 'Publicar oferta',
};

const AnalyticsPage = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/?action=analytics-overview&days=${days}`);
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Error al cargar las estadísticas');
      }
    } catch (err) {
      setError('Error de red al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div>
          <h1>Analíticas del sitio</h1>
          <p>Alcance de mundotrabajos.es: visitas, visitantes y páginas más vistas.</p>
        </div>
        <div className="range-selector">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`range-btn ${days === opt.value ? 'active' : ''}`}
              onClick={() => setDays(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && !data && <div className="empty-state">Cargando estadísticas…</div>}

      {data && (
        <>
          <div className="analytics-stats-grid">
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{data.total_views}</div>
              <div className="analytics-stat-label">Visitas ({data.range_days} días)</div>
            </div>
            <div className="analytics-stat-card highlight">
              <div className="analytics-stat-value">{data.unique_visitors}</div>
              <div className="analytics-stat-label">Visitantes únicos</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{data.today_views}</div>
              <div className="analytics-stat-label">Visitas de hoy</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{data.mobile_share}%</div>
              <div className="analytics-stat-label">Tráfico móvil</div>
            </div>
          </div>

          <div className="analytics-chart-card">
            <div className="chart-header">
              <h2>Visitas y visitantes en el tiempo</h2>
              <p>Evolución diaria de páginas vistas y visitantes únicos.</p>
            </div>
            <div className="analytics-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d7192a" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#d7192a" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#185adb" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#185adb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} tickLine={false} />
                  <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                  <Area type="monotone" name="Visitas" dataKey="views" stroke="#d7192a" fillOpacity={1} fill="url(#colorViews)" strokeWidth={2} />
                  <Area type="monotone" name="Visitantes únicos" dataKey="visitors" stroke="#185adb" fillOpacity={1} fill="url(#colorVisitors)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="analytics-lists-grid">
            <div className="analytics-list-card">
              <div className="chart-header">
                <h2>Páginas más visitadas</h2>
                <p>Top 10 en el periodo seleccionado.</p>
              </div>
              {data.top_pages.length === 0 ? (
                <p className="soft-empty">Todavía no hay suficientes datos.</p>
              ) : (
                <ul className="analytics-rank-list">
                  {data.top_pages.map((row) => (
                    <li key={row.path}>
                      <span>{PAGE_LABELS[row.path] || row.path}</span>
                      <strong>{row.views}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="analytics-list-card">
              <div className="chart-header">
                <h2>Origen del tráfico</h2>
                <p>De dónde llegan tus visitantes.</p>
              </div>
              {Object.keys(data.top_referrers).length === 0 ? (
                <p className="soft-empty">Todavía no hay suficientes datos.</p>
              ) : (
                <ul className="analytics-rank-list">
                  {Object.entries(data.top_referrers).map(([source, count]) => (
                    <li key={source}>
                      <span>{source}</span>
                      <strong>{count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
